<template>
  <div class="payment-rules-table">
    <DataTable
      ref="datatableRef"
      :config="datatableConfig"
      :data="rules"
      :refreshButton="refreshButtonConfig"
      :autoLoad="false"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
      @refresh="loadRules"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '@/grid/DataTable.vue';
import { usePaymentRules } from '@/composables/usePaymentRules';
import { usePayrollPermissions } from '@/composables/usePayrollPermissions';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface PaymentRule {
  id: Id;
  franchise_id?: Id;
  user_id?: Id;
  franchise_name?: string;
  user_name?: string;
  rule_type?: string;
  amount?: number | string;
  meta?: string | null;
  frequency?: string;
  active?: boolean | number | string;
  [key: string]: unknown;
}

interface CellParams {
  value: unknown;
  data: PaymentRule;
}

interface ButtonClickEvent {
  originalEvent?: Event;
  [key: string]: unknown;
}

type DataTableInstance = unknown;

const emit = defineEmits<{
  (e: 'edit', rule: PaymentRule): void;
  (e: 'deactivate', rule: PaymentRule): void;
}>();

const { t } = useI18n();
const { listRules, getRuleTypeLabel, getFrequencyLabel, activateRule, deactivateRule } = usePaymentRules();
const { canManagePaymentRules } = usePayrollPermissions();

const rules = ref<PaymentRule[]>([]);
const datatableRef = ref<DataTableInstance | null>(null);

// DataTable Configuration
const datatableConfig = computed(() => {
  return {
    tableId: 'paymentRulesTable',
    pageLength: 25,
    pagination: true,
    columns: tableColumns.value
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'id',
      title: t('ID'),
      renderType: 'clickable_id',
      sortable: true
    },
    {
      data: 'active',
      title: t('Status'),
      renderType: 'toggle',
      sortable: true,
      toggleCallback: handleToggleStatus
    },
    {
      data: 'franchise_name',
      title: t('Franquia'),
      sortable: true
    },
    {
      data: 'user_name',
      title: t('Colaborador'),
      sortable: true
    },
    {
      data: 'rule_type',
      title: t('Tipo de regra'),
      sortable: true,
      cellRenderer: (params: CellParams) => {
        return getRuleTypeLabel(params.value as string);
      }
    },
    {
      data: 'meta',
      title: t('Estrutura'),
      sortable: false,
      cellRenderer: (params: CellParams) => {
        const meta = params.value as string | null;
        if (!meta) return t('Simples');

        try {
          const parsed = JSON.parse(meta);
          if (parsed && parsed.commission_structure) {
            const type = parsed.commission_structure.type;
            if (type === 'tiered') return t('Escalonado');
            if (type === 'progressive') return t('Progressivo');
          }
        } catch (e) {
          // Invalid JSON, fallback to simple
        }
        return t('Simples');
      }
    },
    {
      data: 'amount',
      title: t('Valor'),
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const ruleType = params.data.rule_type;
        const meta = params.data.meta;

        // If there's a commission structure, show brackets summary
        // Preserves original basis-points handling: bracket.rate is fraction (0.05) → display as percent (5%)
        if (meta) {
          try {
            const parsed = JSON.parse(meta);
            if (parsed && parsed.commission_structure && parsed.commission_structure.brackets) {
              const brackets = parsed.commission_structure.brackets as Array<{ rate: number }>;
              const summary = brackets.map(b => {
                const rate = (b.rate * 100).toFixed(2);
                return `${rate}%`;
              }).join(' → ');
              return summary;
            }
          } catch (e) {
            // Invalid JSON, fallback to simple display
          }
        }

        // Simple commission — amount is fraction (0..1), display as percent
        if (ruleType && ruleType.includes('commission')) {
          return `${(Number(params.value) * 100).toFixed(2)}%`;
        }
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(Number(params.value));
      }
    },
    {
      data: 'frequency',
      title: t('Frequência'),
      sortable: true,
      cellRenderer: (_params: CellParams) => {
        return getFrequencyLabel();
      }
    }
  ];
});

/**
 * Get franchise name from franchise_id using permissionsService
 */
const getFranchiseName = (franchiseId: Id | undefined): string => {
  if (!franchiseId) return '';

  try {
    const franchises = permissionsService.getFranquias() as Record<string, { Dados?: { Nome?: string } }> | null | undefined;
    const franchiseIdStr = String(franchiseId);

    if (franchises && franchises[franchiseIdStr]) {
      return franchises[franchiseIdStr].Dados?.Nome || '';
    }

    return '';
  } catch (err) {
    const e = err as Error;
    log('PaymentRulesTable', 'getFranchiseName', 'ERROR getting franchise name', { franchiseId, error: e.message });
    return '';
  }
};

/**
 * Get employee name from user_id using permissionsService
 */
const getEmployeeName = (userId: Id | undefined, franchiseId: Id | undefined): string => {
  if (!userId || !franchiseId) return '';

  try {
    const employees = permissionsService.getFuncionariosByFranquiaID(Number(franchiseId)) as Record<string, { funcionario_nome?: string }> | null | undefined;

    if (employees && employees[String(userId)]) {
      return employees[String(userId)].funcionario_nome || '';
    }

    return '';
  } catch (err) {
    const e = err as Error;
    log('PaymentRulesTable', 'getEmployeeName', 'ERROR getting employee name', { userId, franchiseId, error: e.message });
    return '';
  }
};

const loadRules = async (): Promise<void> => {
  log('PaymentRulesTable', 'loadRules', 'Loading rules from filter');

  try {
    const result = await listRules() as PaymentRule[];

    // Enrich rules with franchise and employee names from permissionsService
    // Convert active field from string to boolean for toggle switch
    rules.value = result.map(rule => ({
      ...rule,
      franchise_name: getFranchiseName(rule.franchise_id) || rule.franchise_name || 'N/A',
      user_name: getEmployeeName(rule.user_id, rule.franchise_id) || rule.user_name || 'N/A',
      active: rule.active === 1 || rule.active === '1' || rule.active === true
    }));

    log('PaymentRulesTable', 'loadRules', 'Rules loaded', { count: rules.value.length });
  } catch (error) {
    const err = error as Error;
    console.error('Error loading payment rules:', err);
    eventBus.emit('globalMessage.error', {
      message: err.message || t('Falha ao carregar regras de pagamento'),
      duration: 5000
    });
  }
};

const handleEditClick = (ruleId: Id): void => {
  log('PaymentRulesTable', 'handleEditClick', 'Edit clicked for ID', ruleId);
  const rule = rules.value.find(r => r.id === ruleId || String(r.id) === String(ruleId));
  if (rule) {
    emit('edit', rule);
  }
};

const handleToggleStatus = async (rowData: PaymentRule, newValue: boolean): Promise<void> => {
  log('PaymentRulesTable', 'handleToggleStatus', 'Toggle clicked', { ruleId: rowData.id, newValue });

  if (!canManagePaymentRules.value) {
    eventBus.emit('globalMessage.error', {
      message: t('Você não tem permissão para alterar o status desta regra'),
      duration: 3000
    });
    return;
  }

  try {
    if (newValue) {
      await activateRule(Number(rowData.id));
      eventBus.emit('payment-rule.updated', rowData.id);
      eventBus.emit('globalMessage.success', {
        message: t('Regra de pagamento ativada com sucesso'),
        duration: 3000
      });
    } else {
      await deactivateRule(Number(rowData.id));
      eventBus.emit('payment-rule.deactivated', rowData.id);
      eventBus.emit('globalMessage.success', {
        message: t('Regra de pagamento desativada com sucesso'),
        duration: 3000
      });
    }
  } catch (error) {
    const err = error as Error;
    console.error('Error toggling payment rule status:', err);
    eventBus.emit('globalMessage.error', {
      message: err.message || t('Falha ao alterar status da regra de pagamento'),
      duration: 5000
    });
    // Reload to revert the toggle
    loadRules();
  }
};

const handleButtonClick = (event: ButtonClickEvent): void => {
  const target = event.originalEvent?.target as HTMLElement | undefined;
  if (!target) return;

  const button = target.closest('button');
  if (!button) return;

  const ruleId = button.getAttribute('data-id');
  if (!ruleId) return;

  const rule = rules.value.find(r => String(r.id) === ruleId);
  if (!rule) return;

  if (button.classList.contains('action-edit')) {
    log('PaymentRulesTable', 'handleButtonClick', 'Edit clicked', rule);
    emit('edit', rule);
  } else if (button.classList.contains('action-deactivate')) {
    log('PaymentRulesTable', 'handleButtonClick', 'Deactivate clicked', rule);
    emit('deactivate', rule);
  }
};

// Event listeners
onMounted(() => {
  loadRules();

  eventBus.on('payment-rule.created', loadRules);
  eventBus.on('payment-rule.updated', loadRules);
  eventBus.on('payment-rule.activated', loadRules);
  eventBus.on('payment-rule.deactivated', loadRules);
  eventBus.on('payment-rule.deleted', loadRules);
  eventBus.on('filter.applied', loadRules);
});

onUnmounted(() => {
  eventBus.off('payment-rule.created', loadRules);
  eventBus.off('payment-rule.updated', loadRules);
  eventBus.off('payment-rule.activated', loadRules);
  eventBus.off('payment-rule.deactivated', loadRules);
  eventBus.off('payment-rule.deleted', loadRules);
  eventBus.off('filter.applied', loadRules);
});
</script>

<style scoped>
.payment-rules-table {
  width: 100%;
}

:deep(.actions-cell) {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
</style>
